import type {
  ZodAny,
  ZodType,
  ZodArray,
  ZodString,
  ZodNumber,
  ZodObject,
  ZodRecord,
  ZodEffects,
  ZodBoolean,
  ZodTypeDef,
  ZodNullable,
  ZodOptional,
} from 'zod';

import { z as zod } from 'zod';

import { schemaHelper } from 'src/components/hook-form';

type SchemaType =
  | ZodString
  | ZodNullable<ZodString>
  | ZodOptional<ZodString>
  | ZodNumber
  | ZodOptional<ZodNumber>
  | ZodBoolean
  | ZodNullable<ZodType<File, ZodTypeDef, File>>
  | ZodArray<ZodObject<{ id: ZodString; name: ZodString }>>
  | ZodRecord<ZodString, ZodAny>
  | ZodOptional<ZodRecord<ZodString, ZodAny>>
  | ZodEffects<any>
  | ZodEffects<ZodNullable<zod.ZodNumber>, number | null, number | null>
  | zod.ZodOptional<
      zod.ZodEffects<
        zod.ZodNullable<zod.ZodRecord<zod.ZodString, zod.ZodAny>>,
        Record<string, any> | null,
        Record<string, any> | null
      >
    >
  | zod.ZodEffects<
      zod.ZodNullable<zod.ZodRecord<zod.ZodString, zod.ZodAny>>,
      Record<string, any> | null,
      Record<string, any> | null
    >;

type fld = { name: string; required: boolean; maxLimit?: number };

const validationSchema = (fldList: fld[] = [], t: (key: string) => string) => {
  const fldSchema = (name: string, required: boolean = true) => {
    const isFldIncluded = ['comment', 'description', 'templateMsg', 'contactId'].includes(name);
    const maxChar = isFldIncluded ? 1000 : 100;

    const minCharDesc = t('MIN_VAL_ERR').replace('{NO_CHAR}', '2');
    const maxCharDesc = t('MAX_VAL_ERR').replace('{NO_CHAR}', maxChar.toString());
    const fieldLabel = getFldLabel(name, t);

    return required
      ? zod
          .string()
          .nonempty({ message: `${fieldLabel} ${t('IS_REQUIRED')}` })
          .min(isFldIncluded ? 2 : 2, {
            message: isFldIncluded
              ? `${fieldLabel} ${t('IS_REQUIRED')}`
              : `${fieldLabel} ${minCharDesc}`,
          })
          .max(maxChar, { message: `${fieldLabel} ${maxCharDesc}` })
      : zod
          .string()
          .nullable()
          .refine(
            (val) => {
              if (!val) return true; // allow null or empty
              return val.length >= 2;
            },
            { message: `${fieldLabel} ${minCharDesc}` }
          )
          .refine(
            (val) => {
              if (!val) return true;
              return val.length <= maxChar;
            },
            { message: `${fieldLabel} ${maxCharDesc}` }
          );
  };

  const dateSchema = (name: string, required: boolean = true) => {
    const fieldLabel = getFldLabel(name, t);

    if (required) {
      // Schema for required fields
      return zod
        .any()
        .nullable() // Allow null for required fields if needed
        .refine((val) => val !== null, {
          message: `${fieldLabel} ${t('IS_REQUIRED')}`, // Ensures it's not null or empty string
        });
    } else {
      // Schema for optional fields
      return zod.string().nullable(); // Allow null for optional fields without additional validation
    }
  };

  const numberFldSchema = (name: string, required: boolean = true) => {
    const maxCharDesc = t('MAX_VAL_ERR').replace('{NO_CHAR}', '15');

    const fieldLabel = getFldLabel(name, t);
    return required
      ? zod
          .string()
          .min(1, { message: `${getFldLabel(name, t)} ${t('IS_REQUIRED')}` })
          .max(15, { message: `${getFldLabel(name, t)} ${maxCharDesc}` })
      : zod
          .string()
          .nullable()
          .refine(
            (val) => {
              if (!val) return true;
              return val.length <= 20;
            },
            { message: `${fieldLabel} ${maxCharDesc}` }
          );
  };

  const numberStringSchema = (name: string, required: boolean = true, maxLimit: number = 0) => {
    const maxValue = maxLimit || 0;
    const maxValDesc =
      name === 'storageLimit'
        ? t('STORAGE_LIMIT_ERR').replace('{MAXVALUE}', String(maxValue))
        : t('USER_LIMIT_ERR').replace('{MAXVALUE}', String(maxValue));
    const fieldLabel = getFldLabel(name, t);

    const validateNumberString = (val: string | null) => {
      if (!val) return !required;
      const num = Number(val);
      return !isNaN(num) && num <= maxValue;
    };

    return zod
      .string()
      .nullable()
      .refine(validateNumberString, {
        message: `${fieldLabel} ${maxValDesc}`,
      });
  };

  const alphaNumericRegex = /^[A-Za-z0-9]+$/;

  const alphaNumericFldSchema = (
    name: string,
    required: boolean = true,
    minLength: number = 3,
    maxLength: number = 30
  ) => {
    const minCharDesc = t('MIN_VAL_ERR').replace('{NO_CHAR}', `${minLength}`);
    const maxCharDesc = t('MAX_VAL_ERR').replace('{NO_CHAR}', `${maxLength}`);
    const invalidCharMsg = t('ONLY_ALPHABETS_NUMBERS_ALLOWED');

    const schema = zod
      .string()
      .min(minLength, { message: `${getFldLabel(name, t)} ${minCharDesc}` })
      .max(maxLength, { message: `${getFldLabel(name, t)} ${maxCharDesc}` })
      .regex(alphaNumericRegex, { message: `${getFldLabel(name, t)} ${invalidCharMsg}` });

    const nonRequiredSchema = zod
      .string()
      .nullable()
      .refine(
        (val) => {
          if (!val) return true;

          return alphaNumericRegex.test(val);
        },
        { message: `${getFldLabel(name, t)} ${invalidCharMsg}` }
      )
      .refine(
        (val) => {
          if (!val) return true;
          return val.length >= minLength;
        },
        { message: `${getFldLabel(name, t)} ${minCharDesc}` }
      )
      .refine(
        (val) => {
          if (!val) return true;
          return val.length <= maxLength;
        },
        { message: `${getFldLabel(name, t)} ${maxCharDesc}` }
      );

    return required ? schema : nonRequiredSchema;
  };

  const noOfCharSchema = (name: string, min: number = 1, max: number = 6) =>
    zod
      .string()
      .min(min, { message: `${getFldLabel(name, t)} ${t('IS_REQUIRED')}` })
      .max(max, { message: `${getFldLabel(name, t)} ${t('MUST_AT_LEAST_6_CHAR')}` });

  const emailSchema = (name: string, required: boolean = true) =>
    required
      ? zod
          .string()
          .min(1, { message: `${getFldLabel(name, t)} ${t('IS_REQUIRED')}` })
          .email({ message: t('VALID_EMAIL') })
      : zod
          .string()
          .refine((value) => value.length <= 1 || zod.string().email().safeParse(value).success, {
            message: t('VALID_EMAIL'),
          });

  const passwordSchema = (name: string, required: boolean = true) =>
    required
      ? zod
          .string()
          .min(6, { message: `${name} must be at least 6 characters long` })
          .regex(/[a-z]/, { message: `${name} must contain at least one lowercase letter` })
          .regex(/[A-Z]/, { message: `${name} must contain at least one uppercase letter` })
          .regex(/[^a-zA-Z0-9]/, { message: `${name} must contain at least one special character` })
      : zod.string().optional();

  const websiteSchema = (name: string, required: boolean = true) =>
    required
      ? zod
          .string()
          .min(1, { message: `${getFldLabel(name, t)} ${t('IS_REQUIRED')}` })
          .regex(/^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(:\d+)?(\/.*)?$/, {
            message: `${name} ${t('VALID_URL')}`,
          })
      : zod
          .string()
          .refine(
            (value) =>
              value.length === 0 ||
              /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(:\d+)?(\/.*)?$/.test(value),
            { message: `${name} ${t('VALID_URL')}` }
          );

          const stringArraySchema = (name: string, required = true) =>
            zod
              .array(
                zod.string().min(1, { message: `${getFldLabel(name, t)} ${t('IS_REQUIRED')}` })
              )
              .min(1, { message: `${getFldLabel(name, t)} ${t('IS_REQUIRED')}` })
              .transform((val) => val);

  const stringSchema = (
    name: string,
    required: boolean = true,
    minLength: number = 3,
    maxLength: number = 30
  ) => {
    const minCharDesc = t('MIN_VAL_ERR').replace('{NO_CHAR}', `${minLength}`);
    const maxCharDesc = t('MAX_VAL_ERR').replace('{NO_CHAR}', `${maxLength}`);
    return required
      ? zod
          .string()
          .min(1, { message: `${getFldLabel(name, t)} ${t('IS_REQUIRED')}` })
          .regex(/^[A-Za-z\s-]+$/, { message: `${t('INVALID')} ${name}` })
          .max(100, { message: `${getFldLabel(name, t)} ${maxCharDesc}` })
      : zod
          .string()
          .regex(/^[A-Za-z\s-]*$/, { message: `${t('INVALID')} ${name}` })
          .max(100, { message: `${getFldLabel(name, t)} ${maxCharDesc}` });
  };

  const dropDownSchema = (name: string, required: boolean = true) =>
    required
      ? schemaHelper.nullableInput(
          zod.string().min(1, { message: `${getFldLabel(name, t)} ${t('IS_REQUIRED')}` })
        )
      : schemaHelper.nullableInput(zod.string());

  const fileSchema = (name: string, required: boolean) =>
    required
      ? schemaHelper.file({ message: `${getFldLabel(name, t)} ${t('IS_REQUIRED')}` })
      : zod.instanceof(File).nullable();

  const assignUsersSchema = (name: string) => {
    const replaceText = t('NO_OF_USER_USER_MUST_ASSIGNED').replace('{NO_OF_USER}', '1');
    return zod
      .array(zod.object({ id: zod.string(), name: zod.string() }))
      .min(1, { message: replaceText });
  };

  const assignedToSchema = (name: string, required: boolean) => {
    const schema = zod
      .record(zod.any())
      .nullable()
      .refine(
        (value) => {
          if (required && !value) return false;
          return true; // Ensure successful validation returns true
        },
        { message: `${getFldLabel(name, t)} ${t('IS_REQUIRED')}` }
      );

    return required ? schema : schema.optional();
  };

  const returnSchema = (fldName = '', required = false, maxLimit = 0): SchemaType => {
    switch (fldName) {
      case 'date':
        return dateSchema(fldName, required);
      case 'firstName':
      case 'lastName':
      case 'organizationName':
      case 'confirmPassword':
      case 'name':
      case 'campaignName':
      case 'address':
      case 'channelName':
      case 'category':
      case 'teamName':
      case 'labelName':
      case 'labelDesc':
      case 'labelType':
      case 'labelColor':
      case 'userExtId':
      case 'userExtPassword':
      case 'templateName':
      case 'templateMsg':
      case 'labelDescription':
      case 'websiteName':
      case 'websiteDomin':
      case 'widgetColor':
      case 'welcomeTitle':
      case 'welcomeTagline':
      case 'description':
      case 'comment':
      case 'title':
      case 'contactId':
      case 'webhookName':
        return fldSchema(fldName, required);
      case 'password':
        return passwordSchema(fldName, required);
      case 'city':
      case 'country':
        return stringSchema(fldName, required);
      case 'website':
      case 'webhookUrl':
        return websiteSchema(fldName, required);
      case 'events':
        return stringArraySchema(fldName, required);
      case 'companySize':
      case 'phone':
      case 'phonenumber':
      case 'extNumber':
      case 'didNumber':
        return numberFldSchema(fldName, required);
      case 'pincode':
      case 'CRNumber':
      case 'crNumber':
      case 'regNumber':
      case 'nationalId':
        return alphaNumericFldSchema(fldName, required);
      case 'email':
      case 'supportEmail':
        return emailSchema(fldName, required);
      case 'businessIndustry':
      case 'planName':
      case 'maxUsers':
      case 'channel':
      case 'Channel':
      case 'language':
      case 'role':
      case 'roleId':
      case 'status':
      case 'singleSelect':
      case 'companyId':
      case 'assignedType':
      case 'teams':
      case 'DidType':
      case 'DidNumber':
      case 'priority':
      case 'reportTo':
        return dropDownSchema(fldName, required);
      case 'code':
        return noOfCharSchema(fldName);
      case 'ProfileURL':
        return fileSchema(fldName, required);
      case 'assignUsers':
        return assignUsersSchema(fldName);
      // IMAP/SMTP
      case 'smtpEnabled': // SMTP
      case 'imapEnabled':
      case 'imapEnableSsl':
        return zod.boolean();
      case 'smtpAddress':
      case 'smtpPort':
      case 'smtpLogin':
      case 'smtpPassword':
      case 'smtpDomain':
      case 'imapAddress': // IMAP
      case 'imapPort':
      case 'imapLogin':
      case 'imapPassword':
        return !['smtpPort', 'imapPort'].includes(fldName)
          ? zod.string().optional()
          : zod.number().optional();
      case 'assignedId':
      case 'contactId_singleField':
      case 'companyId_singleField':
        return assignedToSchema(fldName, required);
      case 'userLimit':
      case 'storageLimit':
        return numberStringSchema(fldName, required, maxLimit);
      default:
        throw new Error(`Field Validation Not Found : ${fldName}`);
    }
  };

  if (fldList.length > 0) {
    const schemaObj: Record<string, SchemaType> = fldList.reduce(
      (acc, { name, required, maxLimit }) => {
        acc[name] = returnSchema(name, required, maxLimit);
        return acc;
      },
      {} as Record<string, SchemaType>
    );
    return zod.object(schemaObj);
  }
  return zod.object({});
};

const getFldLabel = (fldName: string, t: (key: string) => string) => {
  const labelObj: { [key: string]: string } = {
    firstName: t('FIRST_NAME'),
    lastName: t('LAST_NAME'),
    email: t('EMAIL'),
    password: t('PASSWORD'),
    organizationName: t('ORG_NAME'),
    CRNumber: t('CR_NUMBER'),
    crNumber: t('CR_NUMBER'),
    businessIndustry: t('BUSINESS_INDUSTRY'),
    companySize: t('COMPANY_SIZE'),
    confirmPassword: t('CONFIRM_PWD'),
    code: t('CODE'),
    name: t('ACCOUNT_NAME'),
    phone: t('CONTACT_NUM'),
    phonenumber: t('CONTACT_NUM'),
    companyId: t('ASSIGNED_TO'),
    pincode: t('PINCODE'),
    nationalId: t('NATIONAL_ID'),
    city: t('CITY'),
    country: t('COUNTRY'),
    website: t('WEBSITE'),
    address: t('ADDRESS'),
    channelName: t('CHANNEL_NAME'),
    DidType: t('DID_TYPE'),
    category: t('CATEGORY'),
    avatarUrl: t('AVATAR'),
    language: t('LANGUAGE'),
    teamName: t('TEAM_NAME'),
    status: t('STATUS'),
    assignUsers: t('ASSIGN_USERS'),
    labelName: t('NAME'),
    labelDesc: t('DESCRIPTION'),
    labelType: t('TYPE'),
    labelColor: t('COLOR'),
    extNumber: t('EXT_NUMBER'),
    userExtId: t('PBX_USER_EXT'),
    userExtPassword: t('USER_EXT_PWD'),
    singleSelect: t('SINGLE_SELECT'),
    templateName: t('TEMPLATE_NAME'),
    templateMsg: t('TEMPLATE_MESSAGE'),
    didNumber: t('DID_NUMBER'),
    Channel: t('CHANNEL'),
    channel: t('CHANNEL'),
    websiteDomin: t('WEB_DOMAIN'),
    websiteName: t('WEB_NAME'),
    welcomeTitle: t('WELL_HEADING'),
    welcomeTagline: t('WELL_TAGLINE'),
    assignedId: t('SELECT_USER_TEAM'),
    ProfileURL: t('PROFILE_URL'),
    date: t('DATE_TIME'),
    campaignName: t('CAMPAIGN_NAME'),
    webhookName: t('WEBHOOK_NAME'),
    webhookUrl: t('WEBHOOK_URL'),
    // SMTP
    smtpEnabled: t('SMTP_ENABLED'),
    smtpAddress: t('SMTP_ADDRESS'),
    smtpPort: t('SMTP_PORT'),
    smtpLogin: t('SMTP_LOGIN'),
    smtpPassword: t('SMTP_PASSWORD'),
    smtpDomain: t('SMTP_DOMAIN'),
    // IMAP
    imapEnabled: t('IMAP_ENABLED'),
    imapAddress: t('IMAP_ADDRESS'),
    imapPort: t('IMAP_PORT'),
    imapLogin: t('IMAP_LOGIN'),
    imapPassword: t('IMAP_PASSWORD'),
    imapEnableSsl: t('IMAP_ENABLE_SSL'),

    // TICKET
    title: t('TITLE'),
    contactId: t('CONTACT_ID'),
    priority: t('PRIORITY'),
    contactId_singleField: t('CONTACT_NAME'),
    companyId_singleField: t('ACCOUNT_NAME'),

    userLimit: t('USERLIMIT'),
    storageLimit: t('STORAGELIMIT'),
    description: t('DESCRIPTION'),
    role: t('ROLE'),
  };

  return labelObj[fldName] ? t(labelObj[fldName]) : t('UNKNOWN');
};

export default validationSchema;
